export function toPrettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

/** True when the API rejected auth because the client IP does not match the token/session binding. */
export function isIpMismatchAuthError(responseText: string): boolean {
  const haystack = responseText.toLowerCase();
  return (
    haystack.includes('ip') &&
    (haystack.includes('mismatch') || haystack.includes('not allowed') || haystack.includes('invalid'))
  );
}

export function friendlyIpMismatchAuthHint(): string {
  return (
    'The API bound this token to a different IP address. Sign in again from this browser (not via token.txt from another network), ' +
    'turn off VPN if you use one, or sign in again from this browser. ' +
    'If you use a local auth service, set REACT_APP_LOGIN_PROXY_TARGET to match it; otherwise login and assets should both use the cloud API.'
  );
}

function isNginxGatewayHtml(responseText: string): boolean {
  const t = responseText.toLowerCase();
  return t.includes('<html') && (t.includes('502 bad gateway') || t.includes('503 service unavailable') || t.includes('504 gateway'));
}

function isNginxMethodNotAllowedHtml(responseText: string): boolean {
  const t = responseText.toLowerCase();
  return t.includes('<html') && t.includes('405') && t.includes('not allowed');
}

export function friendlyGatewayFailureSummary(status: number, responseText: string): string {
  if (status === 502 && isNginxGatewayHtml(responseText)) {
    return (
      'The API gateway (nginx) could not reach the backend service (502 Bad Gateway). ' +
      'On the Placy VM this often means the assets catalog on port 3001 is stopped — ask your backend team to restart it, then try again.'
    );
  }
  if (status === 502 || status === 503 || status === 504) {
    return 'The API gateway or upstream service is unavailable. Retry later or check server health with your backend team.';
  }
  return 'The server could not complete the request.';
}

/** When the server returns 4xx/5xx, surface everything the browser exposes (headers are often limited by CORS). */
export function formatHttpErrorDetails(options: {
  requestUrl: string;
  requestPayload: Record<string, unknown>;
  response: Response;
  responseText: string;
}): string {
  const { requestUrl, requestPayload, response, responseText } = options;

  const headerEntries: [string, string][] = [];
  response.headers.forEach((value, key) => {
    headerEntries.push([key, value]);
  });
  headerEntries.sort((a, b) => a[0].localeCompare(b[0]));
  const headersBlock =
    headerEntries.length > 0
      ? headerEntries.map(([k, v]) => `  ${k}: ${v}`).join('\n')
      : '  (none — or only CORS-safe headers are visible; the API can list more via Access-Control-Expose-Headers)';

  const body =
    responseText.length === 0
      ? '(empty body)'
      : (() => {
          try {
            return toPrettyJson(JSON.parse(responseText));
          } catch {
            return responseText;
          }
        })();

  const lines = [
    '--- Request (what was sent) ---',
    `POST ${requestUrl}`,
    '',
    toPrettyJson(requestPayload),
    '',
    '--- Response ---',
    `HTTP ${response.status} ${response.statusText}`,
    '',
    'Response headers (all the browser allows you to read):',
    headersBlock,
    '',
    'Response body:',
    body,
    '',
    '--- Note ---',
    response.status >= 500
      ? 'HTTP 500 means the failure happened on the server. This panel only shows what the API returns; there is no extra detail in the browser unless the backend adds it (errors array, trace id, etc.). Check the API process logs, database logs, or add structured error responses / request-id headers on the server.'
      : 'If the body is generic, the server may omit internal details. Logs on the API still hold the full reason.',
  ];

  return lines.join('\n');
}

function looksLikeJsonSnippet(s: string): boolean {
  const t = s.trim();
  return (t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'));
}

/** Nest/class-validator often returns `message` as a string[] (e.g. `["password should not be empty"]`). */
function stringifyApiErrorMessageCandidate(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter(Boolean);
    if (parts.length > 0) {
      return parts.join('; ');
    }
  }
  return null;
}

function tryUserFacingMessageFromResponseBody(responseText: string): string | null {
  const trimmed = responseText.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    const candidates: unknown[] = [
      parsed.message,
      typeof parsed.error === 'string' ? parsed.error : null,
      parsed.description,
      parsed.detail,
      parsed.title,
    ];
    if (parsed.error && typeof parsed.error === 'object' && !Array.isArray(parsed.error)) {
      const inner = parsed.error as Record<string, unknown>;
      candidates.push(inner.message, inner.description, inner.detail);
    }
    for (const c of candidates) {
      const text = stringifyApiErrorMessageCandidate(c);
      if (text) {
        return text;
      }
    }
  } catch {
    const flat = trimmed.replace(/\s+/g, ' ').trim();
    if (flat && !trimmed.startsWith('<') && flat.length <= 240) {
      return flat;
    }
  }
  return null;
}

function isGenericInternalServerMessage(message: string): boolean {
  const m = message.replace(/\s+/g, ' ').trim().toLowerCase();
  return (
    m === 'internal server error' ||
    m === 'internal server error.' ||
    m === 'server error' ||
    m === 'something went wrong'
  );
}

/** User-facing line when the API only returns a generic Nest/Express 500 message. */
export function friendlyInternalServerErrorSummary(): string {
  return (
    'The assets server hit an internal error (HTTP 500). Try signing out and back in. ' +
    'If it keeps happening, the backend team needs to check the assets service logs (port 3001 on the VM).'
  );
}

/**
 * Plain text for error UI: prefers `message` / `error` / etc. from JSON; short generic line if none.
 */
export function httpResponsePrimaryMessage(status: number, responseText: string): string {
  const extracted = tryUserFacingMessageFromResponseBody(responseText);
  if (extracted) {
    const line = extracted.replace(/\s+/g, ' ').trim();
    if (status >= 500 && isGenericInternalServerMessage(line)) {
      return friendlyInternalServerErrorSummary();
    }
    return line.length > 420 ? `${line.slice(0, 417)}…` : line;
  }
  if (!responseText.trim()) {
    return status >= 500
      ? 'The server could not complete the request.'
      : 'The request was not successful.';
  }
  return status >= 500
    ? 'The server could not complete the request.'
    : 'The request was not successful.';
}

/**
 * Short sign-in style message — no HTTP status line.
 */
export function friendlyHttpFailureSummary(status: number, _statusText: string, responseText: string): string {
  const fromBody = tryUserFacingMessageFromResponseBody(responseText);
  if (fromBody) {
    const safe =
      fromBody.length <= 280 &&
      !/^[\[{]/.test(fromBody) &&
      !looksLikeJsonSnippet(fromBody) &&
      !/\bHTTP\s*\d{3}\b/i.test(fromBody);
    if (safe) {
      return fromBody.length > 220 ? `${fromBody.slice(0, 220)}…` : fromBody;
    }
  }
  if (status === 401 || status === 403) {
    if (isIpMismatchAuthError(responseText)) {
      return friendlyIpMismatchAuthHint();
    }
    return 'Sign-in was rejected. Check your identifier and password, or account access.';
  }
  if (status === 502 || status === 503 || status === 504) {
    return friendlyGatewayFailureSummary(status, responseText);
  }
  if (status === 405 || isNginxMethodNotAllowedHtml(responseText)) {
    return (
      'Sign-in hit the wrong address (HTTP 405). The app is calling an API path this host does not accept. ' +
      'On app.offizz.ai, rebuild with build:prod:app (API base without /dev-api).'
    );
  }
  if (status === 404) {
    return 'The login address could not be reached. Check the API configuration.';
  }
  if (status >= 500) {
    return 'The server could not complete sign-in. Try again later.';
  }
  if (status >= 400 && status < 500) {
    return 'Sign-in could not be completed. Check the information you entered.';
  }
  return 'Sign-in failed.';
}

export function friendlyNetworkErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    if (/failed to fetch/i.test(err.message)) {
      return 'Could not reach the server. Check your connection, proxy, or CORS settings.';
    }
    const m = err.message.trim();
    if (m && m.length <= 200 && !/\bHTTP\s*\d{3}\b/i.test(m)) {
      return m;
    }
  }
  return 'Something went wrong while contacting the server.';
}

export function formatThrownErrorDetails(err: unknown): string {
  if (err instanceof Error) {
    const stack = err.stack?.trim();
    return stack && stack.length > 0 ? stack : `${err.name}: ${err.message}`;
  }
  return String(err);
}

/**
 * One line for narrow UI (picklists, etc.): never dump nginx HTML error pages; keep JSON `message` when present.
 */
export function compactHttpErrorForUi(status: number, statusText: string, responseText: string): string {
  const safeText = typeof statusText === 'string' ? statusText.trim() : '';
  const line = safeText.length > 0 ? `HTTP ${status} ${safeText}` : `HTTP ${status}`;
  const raw = responseText.trim();
  if (!raw) {
    return line;
  }
  const head = raw.slice(0, 800);
  const looksHtml =
    /^\s*<(!DOCTYPE\s+)?html\b/i.test(raw) ||
    /<\s*title\s*>\s*502\s*Bad\s*Gateway/i.test(head) ||
    (raw.includes('<html') && raw.includes('</html>'));
  if (looksHtml) {
    if (status >= 502 && status <= 504) {
      return `${line}: Bad Gateway / gateway timeout — usually the reverse proxy could not reach the API upstream (service down, wrong upstream, or timeout).`;
    }
    return `${line}: The server returned HTML instead of JSON — check proxy routing and API health.`;
  }
  const fromJson = tryUserFacingMessageFromResponseBody(responseText);
  if (fromJson) {
    if (status >= 500 && isGenericInternalServerMessage(fromJson)) {
      return `${line}: ${friendlyInternalServerErrorSummary()}`;
    }
    const m = fromJson.replace(/\s+/g, ' ').trim();
    const snip = m.length > 200 ? `${m.slice(0, 197)}…` : m;
    return `${line}: ${snip}`;
  }
  const flat = raw.replace(/\s+/g, ' ').trim();
  const snip = flat.length > 220 ? `${flat.slice(0, 217)}…` : flat;
  return `${line}: ${snip}`;
}

export function buildFailureSummary(status: number, statusText: string, responseText: string): string {
  const line1 = `HTTP ${status} ${statusText}`;
  if (!responseText.trim()) {
    return line1;
  }
  try {
    const parsed = JSON.parse(responseText) as Record<string, unknown>;
    const msg =
      typeof parsed.message === 'string'
        ? parsed.message
        : typeof parsed.error === 'string'
          ? parsed.error
          : null;
    if (msg) {
      return msg.length > 200 ? `${line1}\n\n${msg.slice(0, 200)}…` : `${line1}\n\n${msg}`;
    }
  } catch {
    // not JSON
  }
  const flat = responseText.replace(/\s+/g, ' ').trim();
  if (flat) {
    const snip = flat.length > 180 ? `${flat.slice(0, 180)}…` : flat;
    return `${line1}\n\n${snip}`;
  }
  return line1;
}
